// Player Pack margin for the builder tab (spec 2026-10-07). Same model as the
// spec's numbers: price = singles total; cost = each garment's own unit cost
// (blank + logo print + handling) + one order's box, card flat fee and shipping
// + the included back print on every top; profit after card %, rep and team
// commission. Every number comes from the live pricing rules.
export type Rules = {
  decoration: { ffTransfer: number; ffPress: number };
  perOrder: { creditCardPercent: number; creditCardFlat: number; boxCharge: number; avgShippingCost: number };
  commissions: { salesRep: number; team: number };
  minimumProfit: number;
};
export type SlotOption = { basePrice: number | null; itemCost: number | null };
export type Margin = { price: number | null; cost: number | null; profit: number | null; belowFloor: boolean; missing: string[] };

// Cents, half away from zero. The nudge absorbs float error (175 × 0.801 −
// 97.4 is 42.774999…, which plain Math.round would print as 42.77).
function round2(x: number): number {
  return Math.round(x * 100 + Math.sign(x) * 1e-7) / 100;
}

export function packMargin(slots: string[], picks: Record<string, SlotOption | undefined>, tops: Set<string>, rules: Rules): Margin {
  const missing = slots.filter((s) => !picks[s] || picks[s]!.basePrice == null || picks[s]!.itemCost == null);
  if (missing.length) return { price: null, cost: null, profit: null, belowFloor: false, missing };
  const price = slots.reduce((sum, s) => sum + (picks[s]!.basePrice as number), 0);
  const backPrint = rules.decoration.ffTransfer + rules.decoration.ffPress;
  const perOrder = rules.perOrder.boxCharge + rules.perOrder.avgShippingCost + rules.perOrder.creditCardFlat;
  const cost = slots.reduce((sum, s) => sum + (picks[s]!.itemCost as number) + (tops.has(s) ? backPrint : 0), 0) + perOrder;
  const deductions = rules.perOrder.creditCardPercent + rules.commissions.salesRep + rules.commissions.team;
  const profit = round2(price * (1 - deductions) - cost);
  return { price, cost: round2(cost), profit, belowFloor: profit < rules.minimumProfit, missing: [] };
}

const RULE_FIELDS: [keyof Rules, string | null][] = [
  ['decoration', 'ffTransfer'], ['decoration', 'ffPress'],
  ['perOrder', 'creditCardPercent'], ['perOrder', 'creditCardFlat'], ['perOrder', 'boxCharge'], ['perOrder', 'avgShippingCost'],
  ['commissions', 'salesRep'], ['commissions', 'team'],
  ['minimumProfit', null],
];

// Pricing-rules doc (GET /api/pricing-rules `rules`, field names as Pricing.tsx)
// → the fields packMargin reads. Any missing or non-numeric field makes the
// rules unusable: the margin shows unknown, never a 0 standing in for a cost.
export function marginRules(raw: Record<string, unknown> | null | undefined): { rules: Rules | null; missing: string[] } {
  if (!raw || typeof raw !== 'object') return { rules: null, missing: ['pricing rules'] };
  const out: Record<string, unknown> = {};
  const missing: string[] = [];
  for (const [group, key] of RULE_FIELDS) {
    const g = raw[group];
    const v = key == null ? g : (g && typeof g === 'object' ? (g as Record<string, unknown>)[key] : undefined);
    if (typeof v !== 'number' || !Number.isFinite(v)) { missing.push(key == null ? group : `${group}.${key}`); continue; }
    if (key == null) out[group] = v;
    else out[group] = { ...((out[group] as object) || {}), [key]: v };
  }
  return missing.length ? { rules: null, missing } : { rules: out as Rules, missing: [] };
}

// Print position v4 needs per logo location (lib/player-pack-templates.js).
const POSITION_FOR_LOGO_LOCATION: Record<string, string> = { FC: 'full-chest', LL: 'left-leg' };

type WarnTemplates = {
  slots: Partial<Record<string, { sourceStyle: string; logoLocation: string }>>;
  packs: { id?: string; label?: string; slots: string[] }[];
};
type WarnOption = { sourceStyle: string; basePrice: number | null };

// Slots a pack uses whose chosen style has no active, priced config at the
// slot's print position — v4 cannot build that garment. A warning, not a block.
export function packBuildWarnings(t: WarnTemplates, options: Partial<Record<string, WarnOption[]>>): string[] {
  const used = new Set(t.packs.flatMap((p) => p.slots));
  const out: string[] = [];
  for (const slot of ['tee', 'shorts', 'hoodie', 'backpack']) {
    const tpl = t.slots[slot];
    if (!used.has(slot) || !tpl) continue;
    const ok = (options[slot] || []).some((o) => o.sourceStyle === tpl.sourceStyle && o.basePrice != null);
    if (!ok) {
      const position = POSITION_FOR_LOGO_LOCATION[tpl.logoLocation] || tpl.logoLocation;
      out.push(`v4 can't build ${slot} (${tpl.sourceStyle}): no active priced config at ${position}`);
    }
  }
  return out;
}
